import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';

const listStyles = 'space-y-2.5';
const listItemStyles = 'flex items-start gap-3 text-slate-700 leading-relaxed';

const BulletList = ({ items }) => (
  <ul className={listStyles}>
    {items.map((item) => (
      <li key={item} className={listItemStyles}>
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#99A18C]" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const GuideSection = ({ title, children, intro = null }) => (
  <section className="space-y-4">
    <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-900 italic leading-tight">
      {title}
    </h2>
    {intro}
    <div className="space-y-4 text-slate-700 leading-relaxed">{children}</div>
  </section>
);

const RejsetipsGuideContent = () => {
  const { language, getLocalizedPath } = useLanguage();

  if (language === 'en') {
    return (
      <div className="space-y-10 md:space-y-12">
        <section className="rounded-[2rem] border border-stone-200 bg-stone-50 p-6 md:p-8 shadow-sm">
          <div className="space-y-5 text-slate-700 leading-relaxed">
            <p className="text-lg md:text-xl">
              Planning a holiday as a wheelchair user often requires a bit of extra preparation. With the right planning, you can avoid many of the challenges that can arise along the way and instead focus on enjoying your holiday.
            </p>
            <p>
              After many years of traveling with my husband, who uses a wheelchair, I have learned how much of a difference the small details can make. Things like door widths, bathroom access, pool lifts, and distances to the beach or restaurants can determine whether a holiday is relaxing or filled with challenges. Therefore, I always recommend thoroughly researching accessibility before booking your trip.
            </p>
          </div>
        </section>

        <GuideSection
          title="Before you book"
          intro={(
            <p>
              It is a good idea to thoroughly research the hotel's accessibility before you book.
            </p>
          )}
        >
          <div className="space-y-3">
            <p className="font-semibold text-slate-900">Check, among other things:</p>
            <BulletList
              items={[
                'Is there step-free access to the hotel?',
                'Does the hotel have an elevator?',
                'Is the bathroom equipped with a roll-in shower?',
                'Are there grab rails by the toilet and shower?',
                'What are the door widths of the room and bathroom?',
                'Are there accessible rooms in the desired room category?',
                'Is there a pool lift at the hotel pool?',
                'How are the access conditions in the surrounding area?'
              ]}
            />
            <p>
              Pictures can be useful, but they don't always tell the whole story. Therefore, I always recommend getting important information confirmed directly by the hotel.
            </p>
          </div>
        </GuideSection>

        <GuideSection
          title="Booking flights"
          intro={(
            <p>
              If you are flying, you should book airport assistance well in advance.
            </p>
          )}
        >
          <div className="space-y-3">
            <p className="font-semibold text-slate-900">Remember to state:</p>
            <BulletList
              items={[
                'Whether you use a manual or electric wheelchair',
                'Whether you can walk short distances',
                'Whether you can manage stairs',
                'Whether you need assistance all the way to the aircraft seat'
              ]}
            />
            <p>
              For electric wheelchairs, it is important to know the battery type and specifications, as the airline often asks for this information.
            </p>
          </div>
        </GuideSection>

        <GuideSection
          title="Mobility aids on your trip"
          intro={(
            <p>
              Make a list of the mobility aids you need during the holiday and what you need to remember from home. An extra wheelchair cushion, for example, can be useful.
            </p>
          )}
        >
          <div className="space-y-3">
            <p className="font-semibold text-slate-900">This could, for example, be:</p>
            <BulletList
              items={[
                'Wheelchair',
                'Electric wheelchair or scooter',
                'Shower bench',
                'Shower chair',
                'Transfer board',
                'Cushion or pressure relief',
                'Chargers and spare parts'
              ]}
            />
            <p>
              A tip from our own travels is to always bring the most important spare parts and take pictures of the equipment before departure. This can make the process easier if, contrary to expectations, damage occurs during transport.
            </p>
          </div>
        </GuideSection>

        <GuideSection
          title="Passport, visa, and travel insurance"
          intro={(
            <p>
              When traveling, it is important to have your passport, any visa requirements, and insurance in order before departure. Rules may vary depending on destination, nationality, and duration of travel, and it is always the traveler's responsibility to ensure that necessary documents are valid.
            </p>
          )}
        >
          <div className="space-y-3">
            <h3 className="text-xl font-bold text-slate-900">Passport</h3>
            <p>
              Always check your passport well in advance of departure.
            </p>
            <p>
              For many European destinations, a valid passport is sufficient, but some countries require the passport to be valid for a specific period after entry or after the planned departure date.
            </p>
            <p>
              Also remember to check the rules for children. Children must generally have their own passport.
            </p>
            <p>
              The name on your flight ticket must match the name in your passport. If you have changed your name or if there is a difference between the names, this should be resolved before the trip.
            </p>
          </div>
        </GuideSection>

        <GuideSection
          title="Travel insurance"
          intro={(
            <p>
              Always check that your travel insurance covers your needs.
            </p>
          )}
        >
          <div className="space-y-3">
            <p className="font-semibold text-slate-900">Pay special attention to:</p>
            <BulletList
              items={[
                'Coverage of mobility aids',
                'Repatriation',
                'Acute illness',
                'Luggage',
                'Any pre-existing conditions'
              ]}
            />
            <p>
              Contact the insurance company if you are in doubt about the coverage.
            </p>
          </div>
        </GuideSection>

        <GuideSection
          title="At the destination"
          intro={(
            <p>
              When you arrive, it can be a good idea to orient yourself about:
            </p>
          )}
        >
          <div className="space-y-3">
            <BulletList
              items={[
                'Nearest pharmacy',
                'Nearest hospital or doctor',
                'Accessible beaches',
                'Accessible toilets in the area',
                'Accessible public transport'
              ]}
            />
            <p>
              Many destinations today offer beach assistance, ramps to the beach, and special beach wheelchairs, making it easier to enjoy your holiday.
            </p>
          </div>
        </GuideSection>

        <GuideSection
          title="During your stay"
          intro={(
            <p>
              If something does not work as expected, you should contact the hotel immediately.
            </p>
          )}
        >
          <p>
            The earlier a problem is reported, the greater the chance that it can be resolved during your stay.
          </p>
        </GuideSection>

        <GuideSection
          title="After the trip"
          intro={(
            <p>
              If you have flown with a wheelchair, you should inspect the equipment thoroughly upon arrival.
            </p>
          )}
        >
          <div className="space-y-3">
            <p className="font-semibold text-slate-900">Check, among other things:</p>
            <BulletList
              items={[
                'Wheels',
                'Armrests',
                'Footrests',
                'Electronics and battery',
                'Charging'
              ]}
            />
            <p>
              Any damage must be reported as soon as possible to the airline or the airport.
            </p>
          </div>
        </GuideSection>

        <section className="rounded-[2rem] bg-[#99A18C] p-6 md:p-8 text-white shadow-lg">
          <div className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-serif font-bold italic leading-tight text-white">
              Travel with peace of mind
            </h2>
            <p className="leading-relaxed text-white/95">
              At Alora Rejser, we know how much of a difference the right information can make. That is why I spend time researching hotels, rooms, pool lifts, beaches, and local areas, so you have a better basis for choosing the holiday that suits your needs.
            </p>
            <p className="leading-relaxed text-white/95">
              My goal is to make it easier for wheelchair users and their families to find accessible hotels and trips where accessibility works in practice – so you can travel with greater peace of mind and fewer worries.
            </p>
            <p className="font-semibold text-white">Have a great holiday!</p>
          </div>
        </section>

        <section className="rounded-[2rem] border border-stone-200 bg-white p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-900 italic leading-tight">
                Do you need help finding the right holiday?
              </h2>
              <p className="text-slate-600 leading-relaxed">
                I am happy to help you find a holiday that suits your needs and the way you want to travel.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to={getLocalizedPath('contact', 'en')}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#99A18C] px-6 py-3 font-bold text-white transition-all hover:bg-[#8a917e]"
              >
                Contact us
                <ArrowRight size={18} />
              </Link>
              <Link
                to={getLocalizedPath('destinations', 'en')}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#99A18C]/25 bg-[#99A18C]/10 px-6 py-3 font-bold text-[#99A18C] transition-all hover:bg-[#99A18C]/15"
              >
                See destinations
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Default Danish
  return (
    <div className="space-y-10 md:space-y-12">
      <section className="rounded-[2rem] border border-stone-200 bg-stone-50 p-6 md:p-8 shadow-sm">
        <div className="space-y-5 text-slate-700 leading-relaxed">
          <p className="text-lg md:text-xl">
            At planlægge en ferie som kørestolsbruger kræver ofte lidt ekstra forberedelse. Med den rette planlægning kan du undgå mange af de udfordringer, der kan opstå undervejs, og i stedet fokusere på at nyde ferien.
          </p>
          <p>
            Efter mange års rejser sammen med min mand, som bruger kørestol, har jeg lært, hvor stor forskel de små detaljer kan gøre. Ting som dørbredder, adgang til badeværelset, poollift og afstande til strand eller restauranter kan være afgørende for, om en ferie bliver afslappende eller fyldt med udfordringer. Derfor anbefaler jeg altid at undersøge tilgængeligheden grundigt, inden rejsen bestilles.
          </p>
        </div>
      </section>

      <GuideSection
        title="Før du booker"
        intro={(
          <p>
            Det er en god idé at undersøge hotellets tilgængelighed grundigt, inden du bestiller.
          </p>
        )}
      >
        <div className="space-y-3">
          <p className="font-semibold text-slate-900">Tjek blandt andet:</p>
          <BulletList
            items={[
              'Er der niveaufri adgang til hotellet?',
              'Har hotellet elevator?',
              'Er badeværelset indrettet med roll-in bruser?',
              'Er der støttehåndtag ved toilet og bruser?',
              'Hvad er dørbredderne på værelse og badeværelse?',
              'Er der handicapvenlige værelser i den ønskede værelseskategori?',
              'Findes der poollift ved hotellets pool?',
              'Hvordan er adgangsforholdene i nærområdet?'
            ]}
          />
          <p>
            Billeder kan være nyttige, men de fortæller ikke altid hele historien. Derfor anbefaler jeg altid at få bekræftet vigtige oplysninger direkte fra hotellet.
          </p>
        </div>
      </GuideSection>

      <GuideSection
        title="Bestilling af fly"
        intro={(
          <p>
            Hvis du skal flyve, bør du bestille lufthavnsassistance i god tid.
          </p>
        )}
      >
        <div className="space-y-3">
          <p className="font-semibold text-slate-900">Husk at oplyse:</p>
          <BulletList
            items={[
              'Om du bruger manuel eller elektrisk kørestol',
              'Om du kan gå korte afstande',
              'Om du kan klare trapper',
              'Om du har brug for hjælp hele vejen til flysædet'
            ]}
          />
          <p>
            Ved elektriske kørestole er det vigtigt at kende batteritype og specifikationer, da flyselskabet ofte spørger efter disse oplysninger.
          </p>
        </div>
      </GuideSection>

      <GuideSection
        title="Hjælpemidler på rejsen"
        intro={(
          <p>
            Lav en liste over de hjælpemidler, du har brug for under ferien, og hvad du skal huske hjemfra. En ekstra kørestolspude kan eksempelvis være nyttig.
          </p>
        )}
      >
        <div className="space-y-3">
          <p className="font-semibold text-slate-900">Det kan eksempelvis være:</p>
          <BulletList
            items={[
              'Kørestol',
              'El-kørestol eller scooter',
              'Badebænk',
              'Badestol',
              'Transferbræt',
              'Pude eller trykaflastning',
              'Opladere og reservedele'
            ]}
          />
          <p>
            Et tip fra egne rejser er altid at medbringe de vigtigste reservedele og tage billeder af hjælpemidlerne inden afrejse. Det kan gøre processen lettere, hvis der mod forventning opstår en skade under transporten.
          </p>
        </div>
      </GuideSection>

      <GuideSection
        title="Pas, visum og rejseforsikring"
        intro={(
          <p>
            Når du skal ud at rejse, er det vigtigt at have styr på pas, eventuelle visumkrav og forsikringer inden afrejse. Reglerne kan variere alt efter destination, nationalitet og rejsens længde, og det er altid den rejsendes eget ansvar at sikre, at de nødvendige dokumenter er gyldige.
          </p>
        )}
      >
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-slate-900">Pas</h3>
          <p>
            Kontrollér altid dit pas i god tid inden afrejse.
          </p>
          <p>
            Til mange europæiske destinationer er et gyldigt dansk pas tilstrækkeligt, men nogle lande kræver, at passet er gyldigt i en bestemt periode efter indrejse eller efter den planlagte hjemrejsedato.
          </p>
          <p>
            Husk også at kontrollere reglerne for børn. Børn skal som udgangspunkt have deres eget pas.
          </p>
          <p>
            Navnet på din flybillet skal stemme overens med navnet i dit pas. Har du skiftet navn, eller er der forskel på navnene, bør dette afklares inden rejsen.
          </p>
        </div>
      </GuideSection>

      <GuideSection
        title="Rejseforsikring"
        intro={(
          <p>
            Kontrollér altid, at din rejseforsikring dækker dine behov.
          </p>
        )}
      >
        <div className="space-y-3">
          <p className="font-semibold text-slate-900">Vær særligt opmærksom på:</p>
          <BulletList
            items={[
              'Dækning af hjælpemidler',
              'Hjemtransport',
              'Akut sygdom',
              'Bagage',
              'Eventuelle eksisterende lidelser'
            ]}
          />
          <p>
            Kontakt forsikringsselskabet, hvis du er i tvivl om dækningen.
          </p>
        </div>
      </GuideSection>

      <GuideSection
        title="På destinationen"
        intro={(
          <p>
            Når du ankommer, kan det være en god idé at orientere dig om:
          </p>
        )}
      >
        <div className="space-y-3">
          <BulletList
            items={[
              'Nærmeste apotek',
              'Nærmeste hospital eller læge',
              'Tilgængelige strande',
              'Handicaptoiletter i området',
              'Tilgængelig offentlig transport'
            ]}
          />
          <p>
            Mange destinationer tilbyder i dag strandhjælp, ramper til stranden og særlige badekørestole, som gør det lettere at nyde ferien.
          </p>
        </div>
      </GuideSection>

      <GuideSection
        title="Under opholdet"
        intro={(
          <p>
            Hvis noget ikke fungerer som forventet, bør du kontakte hotellet med det samme.
          </p>
        )}
      >
        <p>
          Jo tidligere et problem bliver meldt, desto større er chancen for, at det kan løses under opholdet.
        </p>
      </GuideSection>

      <GuideSection
        title="Efter rejsen"
        intro={(
          <p>
            Hvis du har fløjet med kørestol, bør du kontrollere hjælpemidlet grundigt efter ankomst.
          </p>
        )}
      >
        <div className="space-y-3">
          <p className="font-semibold text-slate-900">Tjek blandt andet:</p>
          <BulletList
            items={[
              'Hjul',
              'Armlæn',
              'Fodstøtter',
              'Elektronik og batteri',
              'Opladning'
            ]}
          />
          <p>
            Eventuelle skader skal anmeldes hurtigst muligt til flyselskabet eller lufthavnen.
          </p>
        </div>
      </GuideSection>

      <section className="rounded-[2rem] bg-[#99A18C] p-6 md:p-8 text-white shadow-lg">
        <div className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-serif font-bold italic leading-tight text-white">
            Rejs med ro i maven
          </h2>
          <p className="leading-relaxed text-white/95">
            Hos Alora Rejser ved vi, hvor stor forskel de rigtige oplysninger kan gøre. Derfor bruger jeg tid på at undersøge hoteller, værelser, poollifte, strande og nærområder, så du får et bedre grundlag for at vælge den ferie, der passer til dine behov.
          </p>
          <p className="leading-relaxed text-white/95">
            Mit mål er at gøre det lettere for kørestolsbrugere og deres familier at finde handicapvenlige hoteller og rejser, hvor tilgængeligheden fungerer i praksis – så du kan rejse med større tryghed og færre bekymringer.
          </p>
          <p className="font-semibold text-white">God ferie!</p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-stone-200 bg-white p-6 md:p-8 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-900 italic leading-tight">
              Har du brug for hjælp til at finde den rigtige ferie?
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Jeg hjælper dig gerne med at finde en ferie, der passer til dine behov og til den måde, du ønsker at rejse på.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={getLocalizedPath('contact', 'da')}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#99A18C] px-6 py-3 font-bold text-white transition-all hover:bg-[#8a917e]"
            >
              Kontakt os
              <ArrowRight size={18} />
            </Link>
            <Link
              to={getLocalizedPath('destinations', 'da')}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#99A18C]/25 bg-[#99A18C]/10 px-6 py-3 font-bold text-[#99A18C] transition-all hover:bg-[#99A18C]/15"
            >
              Se rejsemål
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RejsetipsGuideContent;
